import { useEffect, useState } from 'react'

import { AthenaError } from './api/client.ts'
import { useResource } from './useResource.ts'
import type { Resource } from './useResource.ts'

/**
 * `useResource` com recarga periódica, para as estatísticas e o resumo — que
 * custam consulta ao banco e por isso NÃO andam no ritmo do stream (§4.7, §5.2):
 * 30 a 60 s, ou ao abrir a tela.
 *
 * Duas pausas deliberadas:
 *  - aba oculta não pergunta nada (ninguém está olhando, e o próximo foco
 *    recarrega na hora);
 *  - 403 é terminal: falta escopo ou papel, e repetir só repete a recusa.
 */
export function usePolledResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  intervalMs: number,
): Resource<T> & { loadedAt: number | null } {
  const resource = useResource(fetcher)
  const { reload, data, error } = resource
  const [loadedAt, setLoadedAt] = useState<number | null>(null)
  const forbidden = error instanceof AthenaError && error.status === 403

  useEffect(() => {
    if (data !== null) setLoadedAt(Date.now())
  }, [data])

  useEffect(() => {
    if (forbidden || typeof document === 'undefined') return
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') reload()
    }, intervalMs)
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') reload()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [reload, intervalMs, forbidden])

  return { ...resource, loadedAt }
}
